/**
 * Colour parsing and sRGB ↔ OKLab/OKLCH conversion. Zero dependencies.
 *
 * OKLCH is used rather than HSL because dark-mode adaptation needs to change a
 * colour's lightness while holding its perceived hue and colourfulness fixed.
 * HSL's `L` is not perceptual — dropping it on a yellow and on a blue produces
 * wildly different apparent changes — whereas OKLab was fitted to perceptual
 * data precisely so that a single axis behaves consistently across hues.
 *
 * Matrices from Björn Ottosson's OKLab derivation.
 */

export interface Rgb {
  /** 0–255, integer. */
  r: number;
  /** 0–255, integer. */
  g: number;
  /** 0–255, integer. */
  b: number;
}

export interface Oklch {
  /** Perceptual lightness, 0–1. */
  l: number;
  /** Chroma, 0–~0.37 in sRGB. */
  c: number;
  /** Hue in RADIANS. Kept in radians end-to-end — never round-trip via degrees. */
  h: number;
}

/** How many chroma-reduction steps the gamut mapper takes. */
const GAMUT_STEPS = 32;

/**
 * Parses `#RGB`, `#RRGGBB`, `rgb(...)` and `rgba(...)` (including `%`
 * channels). Returns `null` for anything else — callers are expected to fall
 * back to the original string rather than throw, because this runs in render
 * paths fed by backend data.
 */
export function parseCssColorToRgb(color: string): Rgb | null {
  const input = color.trim();
  if (!input) return null;

  const shortHexMatch = /^#([0-9a-f]{3})$/i.exec(input);
  if (shortHexMatch) {
    const hex = shortHexMatch[1] ?? '';
    const [r, g, b] = hex.split('');
    if (r === undefined || g === undefined || b === undefined) return null;
    return {
      r: parseInt(`${r}${r}`, 16),
      g: parseInt(`${g}${g}`, 16),
      b: parseInt(`${b}${b}`, 16),
    };
  }

  const longHexMatch = /^#([0-9a-f]{6})$/i.exec(input);
  if (longHexMatch) {
    const hex = longHexMatch[1] ?? '';
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }

  const rgbaMatch = /^rgba?\((.+)\)$/i.exec(input);
  if (!rgbaMatch) return null;

  const channels = (rgbaMatch[1] ?? '')
    .split(',')
    .map((part) => parseRgbChannel(part.trim()))
    .slice(0, 3);

  const [r, g, b] = channels;
  if (r === undefined || r === null) return null;
  if (g === undefined || g === null) return null;
  if (b === undefined || b === null) return null;

  return { r, g, b };
}

/** Uppercase `#RRGGBB`. */
export function rgbToHex(rgb: Rgb): string {
  return `#${toHexPair(rgb.r)}${toHexPair(rgb.g)}${toHexPair(rgb.b)}`.toUpperCase();
}

/** sRGB channel (0–1) → linear-light. */
export function srgbChannelToLinear(channel: number): number {
  return channel <= 0.04045
    ? channel / 12.92
    : Math.pow((channel + 0.055) / 1.055, 2.4);
}

/** Linear-light channel → sRGB (0–1). */
export function linearChannelToSrgb(channel: number): number {
  return channel <= 0.0031308
    ? 12.92 * channel
    : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055;
}

export function srgbToOklch(rgb: Rgb): Oklch {
  const red = srgbChannelToLinear(rgb.r / 255);
  const green = srgbChannelToLinear(rgb.g / 255);
  const blue = srgbChannelToLinear(rgb.b / 255);

  const longCone = Math.cbrt(
    0.4122214708 * red + 0.5363325363 * green + 0.0514459929 * blue,
  );
  const mediumCone = Math.cbrt(
    0.2119034982 * red + 0.6806995451 * green + 0.1073969566 * blue,
  );
  const shortCone = Math.cbrt(
    0.0883024619 * red + 0.2817188376 * green + 0.6299787005 * blue,
  );

  const l =
    0.2104542553 * longCone + 0.793617785 * mediumCone - 0.0040720468 * shortCone;
  const a =
    1.9779984951 * longCone - 2.428592205 * mediumCone + 0.4505937099 * shortCone;
  const b =
    0.0259040371 * longCone + 0.7827717662 * mediumCone - 0.808675766 * shortCone;

  return { l, c: Math.hypot(a, b), h: Math.atan2(b, a) };
}

/**
 * OKLCH → sRGB, gamut-mapped.
 *
 * sRGB has very little chroma volume near white, so a high-lightness colour
 * often cannot keep its chroma. Rather than clipping channels — which shifts
 * the hue — chroma is reduced by binary search at fixed lightness and hue, so
 * the hue survives exactly and only colourfulness is traded away.
 */
export function oklchToSrgb(oklch: Oklch): Rgb {
  if (isInSrgbGamut(oklch)) {
    return quantize(oklchToLinearRgb(oklch));
  }

  let low = 0;
  let high = oklch.c;
  for (let step = 0; step < GAMUT_STEPS; step += 1) {
    const mid = (low + high) / 2;
    if (isInSrgbGamut({ l: oklch.l, c: mid, h: oklch.h })) {
      low = mid;
    } else {
      high = mid;
    }
  }

  return quantize(oklchToLinearRgb({ l: oklch.l, c: low, h: oklch.h }));
}

/** Linear-light sRGB triple for an OKLCH colour. Channels may fall outside 0–1. */
export function oklchToLinearRgb(oklch: Oklch): [number, number, number] {
  const a = oklch.c * Math.cos(oklch.h);
  const b = oklch.c * Math.sin(oklch.h);

  const longCone = Math.pow(oklch.l + 0.3963377774 * a + 0.2158037573 * b, 3);
  const mediumCone = Math.pow(oklch.l - 0.1055613458 * a - 0.0638541728 * b, 3);
  const shortCone = Math.pow(oklch.l - 0.0894841775 * a - 1.291485548 * b, 3);

  return [
    4.0767416621 * longCone - 3.3077115913 * mediumCone + 0.2309699292 * shortCone,
    -1.2684380046 * longCone + 2.6097574011 * mediumCone - 0.3413193965 * shortCone,
    -0.0041960863 * longCone - 0.7034186147 * mediumCone + 1.707614701 * shortCone,
  ];
}

function isInSrgbGamut(oklch: Oklch): boolean {
  const epsilon = 1e-6;
  return oklchToLinearRgb(oklch).every(
    (channel) => channel >= -epsilon && channel <= 1 + epsilon,
  );
}

function quantize(linear: [number, number, number]): Rgb {
  const [red, green, blue] = linear;
  return {
    r: toByte(linearChannelToSrgb(red)),
    g: toByte(linearChannelToSrgb(green)),
    b: toByte(linearChannelToSrgb(blue)),
  };
}

function toByte(srgbChannel: number): number {
  return Math.max(0, Math.min(255, Math.round(srgbChannel * 255)));
}

function toHexPair(channel: number): string {
  return Math.max(0, Math.min(255, Math.round(channel)))
    .toString(16)
    .padStart(2, '0');
}

function parseRgbChannel(value: string): number | null {
  if (!value) return null;
  const raw = value.endsWith('%')
    ? (Number(value.slice(0, -1)) * 255) / 100
    : Number(value);
  if (!Number.isFinite(raw)) return null;
  return Math.max(0, Math.min(255, Math.round(raw)));
}
